/**
 * CreateDataTab.jsx
 * -----------------
 * Tab for creating new data tables with manual form or CSV import
 */

import React, { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Plus, Trash2, Upload, Database, Sparkles } from 'lucide-react';
import CSVImporter from './CSVImporter';
import dataManagementService from '../../services/dataManagementService';
import { generateSampleData } from '../../services/openaiService';
import { toast } from 'react-toastify';

const CreateDataTab = ({ appPrefix, userId, onTableCreated }) => {
  const [searchParams] = useSearchParams();
  const appName = searchParams.get('app');
  const [tableName, setTableName] = useState('');
  const [fields, setFields] = useState([
    { name: '', type: 'text', required: false, options: [] }
  ]);
  const [creating, setCreating] = useState(false);
  const [generatingSample, setGeneratingSample] = useState(false);
  const [showCSVImporter, setShowCSVImporter] = useState(false);
  const [errors, setErrors] = useState({});

  const fieldTypes = [
    { value: 'text', label: 'Text' },
    { value: 'textarea', label: 'Text Area' },
    { value: 'number', label: 'Number' },
    { value: 'currency', label: 'Currency' },
    { value: 'percentage', label: 'Percentage' },
    { value: 'rating', label: 'Rating (1-5)' },
    { value: 'email', label: 'Email' },
    { value: 'phone', label: 'Phone' },
    { value: 'url', label: 'URL' },
    { value: 'date', label: 'Date' },
    { value: 'datetime', label: 'Date & Time' },
    { value: 'boolean', label: 'Boolean' },
    { value: 'select', label: 'Select (Dropdown)' },
    { value: 'multiselect', label: 'Multi-Select' },
    { value: 'color', label: 'Color' },
    { value: 'file', label: 'File URL' },
    { value: 'image', label: 'Image URL' },
    { value: 'json', label: 'JSON' }
  ];

  const addField = () => {
    setFields([...fields, { name: '', type: 'text', required: false, options: [] }]);
  };

  const removeField = (index) => {
    if (fields.length > 1) {
      setFields(fields.filter((_, i) => i !== index));
    }
  };

  const updateField = (index, property, value) => {
    const newFields = [...fields];
    newFields[index][property] = value;
    
    // Clear options if changing from select/multiselect to another type
    if (property === 'type' && !['select', 'multiselect'].includes(value)) {
      newFields[index].options = [];
    }
    
    setFields(newFields);
  };

  const updateFieldOptions = (index, optionsString) => {
    const newFields = [...fields];
    // Split by comma and trim each option
    newFields[index].options = optionsString
      .split(',')
      .map(opt => opt.trim())
      .filter(opt => opt.length > 0);
    setFields(newFields);
  };

  const validateForm = () => {
    const newErrors = {};

    // Validate table name
    if (!tableName.trim()) {
      newErrors.tableName = 'Table name is required';
    } else if (!/^[a-zA-Z][a-zA-Z0-9_]*$/.test(tableName)) {
      newErrors.tableName = 'Table name must start with a letter and contain only letters, numbers, and underscores';
    }

    // Validate fields
    const fieldNames = new Set();
    fields.forEach((field, index) => {
      if (!field.name.trim()) {
        newErrors[`field_${index}_name`] = 'Field name is required';
      } else if (!/^[a-zA-Z][a-zA-Z0-9_]*$/.test(field.name)) {
        newErrors[`field_${index}_name`] = 'Field name must start with a letter and contain only letters, numbers, and underscores';
      } else if (fieldNames.has(field.name.toLowerCase())) {
        newErrors[`field_${index}_name`] = 'Field name must be unique';
      }
      fieldNames.add(field.name.toLowerCase());
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleCreateTable = async () => {
    if (!validateForm()) {
      toast.error('Please fix the errors before creating the table');
      return;
    }

    setCreating(true);

    try {
      // Create table schema
      const result = await dataManagementService.createTableSchema(
        appPrefix,
        tableName,
        fields.filter(f => f.name.trim()), // Only include fields with names
        userId
      );

      toast.success(`Table "${result.tableName}" created successfully!`);

      // Ask user if they want to generate sample data
      const wantSample = window.confirm(
        'Table created successfully! Would you like to generate sample data using AI?'
      );

      if (wantSample) {
        await handleGenerateSampleData(result.tableName, fields.filter(f => f.name.trim()));
      }

      // Reset form
      setTableName('');
      setFields([{ name: '', type: 'text', required: false }]);
      setErrors({});

      // Notify parent
      onTableCreated();
    } catch (error) {
      console.error('Failed to create table:', error);
      toast.error('Failed to create table: ' + error.message);
    } finally {
      setCreating(false);
    }
  };

  const handleGenerateSampleData = async (tableNameToUse, fieldsToUse) => {
    setGeneratingSample(true);

    try {
      toast.info('Generating sample data with AI...');
      
      const sampleRecords = await generateSampleData(tableNameToUse, fieldsToUse, 15);

      if (sampleRecords.length === 0) {
        toast.warning('AI did not generate any sample data');
        return;
      }

      // Insert sample records
      await dataManagementService.bulkCreateRecords(
        appPrefix,
        tableNameToUse,
        sampleRecords,
        userId
      );

      toast.success(`Generated and inserted ${sampleRecords.length} sample records!`);
    } catch (error) {
      console.error('Failed to generate sample data:', error);
      toast.error('Failed to generate sample data: ' + error.message);
    } finally {
      setGeneratingSample(false);
    }
  };

  const handleCSVImport = async ({ tableName: csvTableName, file }) => {
    setShowCSVImporter(false);
    setCreating(true);

    try {
      console.log('[CreateDataTab] Starting CSV import:', { csvTableName, appPrefix, userId });
      
      // Use the API's CSV import endpoint which handles everything
      const result = await dataManagementService.importCSVCreateTable(
        file,
        appPrefix,
        userId,
        csvTableName
      );

      console.log('[CreateDataTab] CSV import result:', result);

      toast.success(`Table "${result.displayName || csvTableName}" created from CSV!`);
      
      if (result.recordsCreated && result.recordsCreated > 0) {
        toast.success(`✅ Imported ${result.recordsCreated} records!`);
      } else {
        toast.warning('Table created but no records were imported. Check your CSV format.');
      }

      // Notify parent
      onTableCreated();
    } catch (error) {
      console.error('Failed to import CSV:', error);
      toast.error('Failed to import CSV: ' + error.message);
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="create-data-tab" style={{ padding: '24px' }}>
      {/* Import CSV Button - Top of form */}
      <div style={{ marginBottom: '24px', display: 'flex', justifyContent: 'flex-end' }}>
        <button
          onClick={() => setShowCSVImporter(true)}
          style={{
            padding: '10px 20px',
            background: '#3B82F6',
            border: 'none',
            borderRadius: '8px',
            color: '#FFFFFF',
            fontSize: '14px',
            fontWeight: '600',
            cursor: 'pointer',
            transition: 'all 0.2s ease',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)'
          }}
          onMouseEnter={(e) => {
            e.target.style.background = '#2563EB';
            e.target.style.transform = 'translateY(-1px)';
            e.target.style.boxShadow = '0 4px 6px rgba(0, 0, 0, 0.15)';
          }}
          onMouseLeave={(e) => {
            e.target.style.background = '#3B82F6';
            e.target.style.transform = 'translateY(0)';
            e.target.style.boxShadow = '0 2px 4px rgba(0, 0, 0, 0.1)';
          }}
        >
          <Upload size={18} />
          Import from CSV
        </button>
      </div>

      <div className="create-form">
        {/* Table Name */}
        <div className="form-section">
          <h3>Table Information</h3>
          <div className="form-group">
            <label>
              Table Name <span className="required">*</span>
            </label>
            <input
              type="text"
              value={tableName}
              onChange={(e) => setTableName(e.target.value)}
              placeholder="e.g., users, products, orders"
              className={`form-input ${errors.tableName ? 'input-error' : ''}`}
            />
            {errors.tableName && (
              <span className="error-message">{errors.tableName}</span>
            )}
            <p className="form-hint">
              Will be created as: <code>{appPrefix}_data_{tableName || 'tablename'}</code>
            </p>
          </div>
        </div>

        {/* System Fields Info */}
        <div className="form-section">
          <h3>System Fields (Auto-managed)</h3>
          <div className="system-fields-info">
            <div className="system-field">
              <span className="field-name">id</span>
              <span className="field-type">Auto-generated</span>
              <span className="field-desc">Unique identifier</span>
            </div>
            <div className="system-field">
              <span className="field-name">createdBy</span>
              <span className="field-type">User ID</span>
              <span className="field-desc">Who created this record</span>
            </div>
            <div className="system-field">
              <span className="field-name">createdAt</span>
              <span className="field-type">Timestamp</span>
              <span className="field-desc">When created</span>
            </div>
            <div className="system-field">
              <span className="field-name">updatedBy</span>
              <span className="field-type">User ID</span>
              <span className="field-desc">Who last updated</span>
            </div>
            <div className="system-field">
              <span className="field-name">updatedAt</span>
              <span className="field-type">Timestamp</span>
              <span className="field-desc">When last updated</span>
            </div>
          </div>
        </div>

        {/* Custom Fields */}
        <div className="form-section">
          <h3>Custom Fields</h3>
          <div className="fields-list">
            {fields.map((field, index) => (
              <div key={index} className="field-item">
                <div className="field-inputs">
                  <div className="field-input-group">
                    <label>Field Name</label>
                    <input
                      type="text"
                      value={field.name}
                      onChange={(e) => updateField(index, 'name', e.target.value)}
                      placeholder="e.g., name, email, age"
                      className={`form-input ${errors[`field_${index}_name`] ? 'input-error' : ''}`}
                    />
                    {errors[`field_${index}_name`] && (
                      <span className="error-message">{errors[`field_${index}_name`]}</span>
                    )}
                  </div>
                  
                  <div className="field-input-group">
                    <label>Type</label>
                    <select
                      value={field.type}
                      onChange={(e) => updateField(index, 'type', e.target.value)}
                      className="form-select"
                    >
                      {fieldTypes.map(type => (
                        <option key={type.value} value={type.value}>
                          {type.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  
                  <div className="field-input-group field-required">
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        checked={field.required}
                        onChange={(e) => updateField(index, 'required', e.target.checked)}
                      />
                      Required
                    </label>
                  </div>
                </div>
                
                {/* Options input for select/multiselect */}
                {(field.type === 'select' || field.type === 'multiselect') && (
                  <div className="field-options-input">
                    <label>Options (comma-separated)</label>
                    <input
                      type="text"
                      value={(field.options || []).join(', ')}
                      onChange={(e) => updateFieldOptions(index, e.target.value)}
                      placeholder="e.g., Option1, Option2, Option3"
                      className="form-input"
                    />
                    <p className="form-hint">
                      Enter options separated by commas
                    </p>
                  </div>
                )}
                
                <button
                  onClick={() => removeField(index)}
                  className="btn-remove-field"
                  disabled={fields.length === 1}
                  title="Remove field"
                >
                  <Trash2 size={18} />
                </button>
              </div>
            ))}
          </div>

          <button onClick={addField} className="btn-add-field">
            <Plus size={18} />
            Add Field
          </button>
        </div>

        {/* Action Buttons */}
        <div className="form-actions">
          <button
            onClick={handleCreateTable}
            disabled={creating || generatingSample}
            className="btn-create-table"
          >
            {creating ? (
              <>
                <div className="spinner-small"></div>
                Creating...
              </>
            ) : (
              <>
                <Database size={18} />
                Create Table
              </>
            )}
          </button>

          {generatingSample && (
            <div className="generating-indicator">
              <Sparkles size={18} className="sparkle-icon" />
              Generating sample data...
            </div>
          )}
        </div>
      </div>

      {/* CSV Importer Modal */}
      {showCSVImporter && (
        <CSVImporter
          onImport={handleCSVImport}
          onCancel={() => setShowCSVImporter(false)}
        />
      )}
    </div>
  );
};

export default CreateDataTab;

